import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface Props {
    evictionsByMonth: [string, number][];
    preventionDataByMonth: [string, number][];
    yearSelected: string | null;
    selectedRegion: string | null;
    evictionsByYearPerMonth: Record<string, Record<string, number>>;
    preventionsByYearPerMonth: Record<string, Record<string, number>>;
    evictionsByRegionPerYearPerMonth: Record<string, Record<string, Record<string, number>>>;
    preventionsByRegionPerYearPerMonth: Record<string, Record<string, Record<string, number>>>;
}

const colorScale = ["#8FA7C7", "#E2E8F0", "#9DBAD5", "#B7C8DB", "#98AFC7", "#C0D3E0"];

const EvictionsByMonthChart = ({
    evictionsByMonth,
    preventionDataByMonth,
    yearSelected,
    selectedRegion,
    evictionsByYearPerMonth,
    preventionsByYearPerMonth,
    evictionsByRegionPerYearPerMonth,
    preventionsByRegionPerYearPerMonth
}: Props) => {
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
    const [selectedEvictions, setSelectedEvictions] = useState<number | null>(null);
    const [selectedPreventions, setSelectedPreventions] = useState<number | null>(null);
    const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

    const formatNumber = (num: number) => {
        if (num >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
        if (num >= 1_000) return (num / 1_000).toFixed(0) + 'K';
        return num.toString();
    };

    const [chartData, setChartData] = useState<any>({
        labels: [],
        datasets: [],
    });

    useEffect(() => {
        let newEvictionsByMonth: Record<string, number> = {};
        let newPreventionsByMonth: Record<string, number> = {};

        if (yearSelected && selectedRegion) {
            newEvictionsByMonth =
                evictionsByRegionPerYearPerMonth[selectedRegion]?.[yearSelected] || {};
            newPreventionsByMonth =
                preventionsByRegionPerYearPerMonth[selectedRegion]?.[yearSelected] || {};
        } else if (yearSelected) {
            newEvictionsByMonth = evictionsByYearPerMonth[yearSelected] || {};
            newPreventionsByMonth = preventionsByYearPerMonth[yearSelected] || {};
        } else {
            newEvictionsByMonth = Object.fromEntries(evictionsByMonth);
            newPreventionsByMonth = Object.fromEntries(preventionDataByMonth);
        }

        const allMonths = Array.from(new Set([
            ...Object.keys(newEvictionsByMonth),
            ...Object.keys(newPreventionsByMonth)
        ])).sort((a, b) =>
            new Date(`1 ${a} 2000`).getMonth() - new Date(`1 ${b} 2000`).getMonth()
        );

        const evictionsData = allMonths.map(month => newEvictionsByMonth[month] || 0);
        const preventionsData = allMonths.map(month => newPreventionsByMonth[month] || 0);

        const getColorScale = (values: number[]) => {
            const min = Math.min(...values);
            const max = Math.max(...values);
            const steps = colorScale.length;
            const stepSize = (max - min) / steps;

            return values.map(value => {
                const index = Math.floor((value - min) / stepSize);
                return colorScale[Math.min(index, colorScale.length - 1)];
            });
        };

        setChartData({
            labels: allMonths,
            datasets: [
                {
                    label: "Evictions",
                    data: evictionsData,
                    backgroundColor: getColorScale(evictionsData),
                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: 4,
                    hoverBackgroundColor: 'rgba(0, 0, 0, 0.5)',
                    categoryPercentage: 0.8,
                    barPercentage: 0.6,
                },
                {
                    label: "Preventions",
                    data: preventionsData,
                    backgroundColor: getColorScale(preventionsData),
                    borderColor: "#fff",
                    borderWidth: 1,
                    borderRadius: 4,
                    hoverBackgroundColor: 'rgba(0, 0, 0, 0.5)',
                    categoryPercentage: 0.8,
                    barPercentage: 0.6,
                }
            ],
        });
    }, [yearSelected, selectedRegion, evictionsByRegionPerYearPerMonth, evictionsByYearPerMonth, preventionsByYearPerMonth]);

    const handleBarClick = (event: any, elements: any[]) => {
        if (elements.length > 0) {
            const index = elements[0].index;
            setSelectedMonth(chartData.labels[index]);
            setSelectedEvictions(chartData.datasets[0].data[index]);
            setSelectedPreventions(chartData.datasets[1].data[index]);
            setHighlightedIndex(index);
        }
    };

    return (
        <div className="p-4 bg-white shadow-md rounded-md">
            {evictionsByMonth.length === 0 && preventionDataByMonth.length === 0 ? (
                <div className="flex items-center justify-center h-[200px] gap-2 text-sm text-gray-500">
                    <svg className="w-5 h-5 animate-spin text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Loading chart data...
                </div>
            ) : (
                <div className="relative w-full" style={{ height: "200px" }}>
                    <Bar
                        data={chartData}
                        options={{
                            maintainAspectRatio: false,
                            responsive: true,
                            plugins: {
                                title: {
                                    display: true,
                                    text: selectedRegion && yearSelected
                                        ? `Evictions, Preventions - ${yearSelected} (${selectedRegion})`
                                        : selectedRegion
                                            ? `Evictions, Preventions (${selectedRegion})`
                                            : yearSelected
                                                ? `Evictions, Preventions (${yearSelected})`
                                                : "Evictions, Preventions - All Years (All Regions)",
                                    font: {
                                        size: 12,
                                        weight: 'bold'
                                    },
                                    padding: {
                                        top: 0,
                                        bottom: 0
                                    },
                                    align: 'center'
                                },
                                legend: {
                                    position: 'top',
                                    labels: {
                                        boxWidth: 12,
                                        padding: 20,
                                        font: {
                                            size: 10
                                        },
                                        usePointStyle: true,
                                        generateLabels: (chart) => {
                                            const original = ChartJS.defaults.plugins.legend.labels.generateLabels(chart)
                                            return original.map((labelItem) => {
                                                let newText = labelItem.text;

                                                if (selectedMonth && selectedEvictions !== null && selectedPreventions !== null) {
                                                    if (labelItem.text === "Evictions") {
                                                        newText = `Evictions: ${selectedEvictions.toLocaleString()}`; // format with commas
                                                    }
                                                    if (labelItem.text === "Preventions") {
                                                        newText = `Preventions: ${selectedPreventions.toLocaleString()}`; // format with commas
                                                    }
                                                }

                                                return {
                                                    ...labelItem,
                                                    text: newText,
                                                };
                                            });
                                        }
                                    }
                                },
                                tooltip: {
                                    backgroundColor: '#fff',
                                    borderColor: '#ddd',
                                    borderWidth: 1,
                                    titleColor: '#333',
                                    bodyColor: '#333',
                                    padding: 10,
                                    callbacks: {
                                        label: (tooltipItem: any) =>
                                            `${tooltipItem.dataset.label}: ${tooltipItem.raw.toLocaleString()}`,
                                    },
                                },
                            },
                            scales: {
                                x: {
                                    grid: {
                                        display: false
                                    },
                                    ticks: {
                                        font: {
                                            size: 10
                                        },
                                        autoSkip: false,
                                        maxRotation: 30,
                                        minRotation: 30
                                    }
                                },
                                y: {
                                    beginAtZero: true,
                                    grid: {
                                        display: false
                                    },
                                    ticks: {
                                        font: {
                                            size: 10
                                        },
                                        callback: (value) => formatNumber(value as number),
                                    },
                                },
                            },
                            onClick: handleBarClick,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default EvictionsByMonthChart;